export type PredictionFeedbackKind = 'phrase_completion' | 'token_prediction' | 'shortcut';

// Suggestion interactions are telemetry-only and should not be treated as
// committed communication history for model training.
export type PredictionFeedbackEvent = {
  id: string;
  phrase: string;
  locale: string;
  kind: PredictionFeedbackKind;
  createdAt: number;
};

export type PredictionFeedbackInput = Omit<PredictionFeedbackEvent, 'id' | 'createdAt'> & {
  id?: string;
  createdAt?: number;
};

export const MAX_PREDICTION_FEEDBACK_EVENTS = 24;

const BASE_STORAGE_KEY = 'ecovoz:prediction-feedback';

let storageKey = BASE_STORAGE_KEY;

const normalizeText = (value: string): string => value.trim().replace(/\s+/g, ' ');
const normalizeLocale = (value: string): string => value.trim().toLowerCase();

function isValidFeedbackKind(value: unknown): value is PredictionFeedbackKind {
  return value === 'phrase_completion' || value === 'token_prediction' || value === 'shortcut';
}

function isValidPredictionFeedbackEvent(value: unknown): value is PredictionFeedbackEvent {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === 'string'
    && typeof obj.phrase === 'string'
    && typeof obj.locale === 'string'
    && isValidFeedbackKind(obj.kind)
    && typeof obj.createdAt === 'number'
  );
}

function loadFromStorage(): PredictionFeedbackEvent[] {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(isValidPredictionFeedbackEvent);
  } catch {
    return [];
  }
}

function saveToStorage(events: PredictionFeedbackEvent[]): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(events));
  } catch {
    // quota exceeded or localStorage unavailable — fail silently
  }
}

const feedbackHistory: PredictionFeedbackEvent[] = loadFromStorage();

export function setPredictionFeedbackOwner(userId: string): void {
  storageKey = `${BASE_STORAGE_KEY}:${userId}`;
  const loaded = loadFromStorage();
  feedbackHistory.length = 0;
  for (const event of loaded) {
    feedbackHistory.push(event);
  }
}

export function clearPredictionFeedbackOwner(): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // unavailable — fail silently
  }

  feedbackHistory.length = 0;
  storageKey = BASE_STORAGE_KEY;
}

export const createPredictionFeedbackId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `feedback-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

export function recordPredictionFeedback(input: PredictionFeedbackInput): PredictionFeedbackEvent {
  const event: PredictionFeedbackEvent = {
    id: input.id ?? createPredictionFeedbackId(),
    phrase: normalizeText(input.phrase),
    locale: normalizeLocale(input.locale || 'pt'),
    kind: input.kind,
    createdAt: input.createdAt ?? Date.now(),
  };

  feedbackHistory.unshift(event);

  if (feedbackHistory.length > MAX_PREDICTION_FEEDBACK_EVENTS) {
    feedbackHistory.length = MAX_PREDICTION_FEEDBACK_EVENTS;
  }

  saveToStorage(feedbackHistory);
  return event;
}

export function getPredictionFeedback(): PredictionFeedbackEvent[] {
  return [...feedbackHistory];
}

export function clearPredictionFeedback(): void {
  feedbackHistory.length = 0;
  saveToStorage([]);
}
